import { FC, useContext } from "react";
import { Controller } from "react-hook-form";
import FormFieldsContainer from "../../../../components/ui/FormFieldsContainer";
import { AsyncSelect, Form, FormField, ModalDialog, Numeric } from "@rothoblaas/design-system";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import useAsyncOptions from "../../../../hooks/useAsyncOptions";
import useI18n from "../../../../hooks/useTranslations";
import {
  InsertSubstructureMaterialClassBody,
  SubstructureMaterialClass,
  UpdateSubstructureMaterialClassBody,
} from "../../../../http/deckDesigner/types/substructureMaterialClasses";
import { SubstructureMaterial } from "../../../../http/deckDesigner/types/substructureMaterials";
import useDeckSubstructureMaterialClassesApi from "../../../../http/deckDesigner/useSubstructureMaterialClasses";
import useDeckSubstructureMaterialsApi from "../../../../http/deckDesigner/useSubstructureMaterials";
import TranslatableTextField from "../../../../components/ui/TranslatableTextField";

interface SubstructureMaterialClassFormBody {
  name: string | null;
  nameIt: string | null;
  nameDe: string | null;
  nameEs: string | null;
  nameFr: string | null;
  modulusOfElasticity: number | null;
  substructureMaterial: SubstructureMaterial | null;
}

interface SubstructureMaterialClassFormProps {
  substructureMaterialClass?: SubstructureMaterialClass;
  onClose: () => void;
}

const SubstructureMaterialClassForm: FC<SubstructureMaterialClassFormProps> = ({
  substructureMaterialClass,
  onClose,
}) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const substructureMaterialClassesApi = useDeckSubstructureMaterialClassesApi();
  const substructureMaterialsApi = useDeckSubstructureMaterialsApi();
  const substructureMaterialOptions = useAsyncOptions(substructureMaterialsApi.getDeckSubstructureMaterials);

  const defaultValues: SubstructureMaterialClassFormBody = {
    name: substructureMaterialClass?.name ?? null,
    nameIt: substructureMaterialClass?.nameIt ?? null,
    nameDe: substructureMaterialClass?.nameDe ?? null,
    nameEs: substructureMaterialClass?.nameEs ?? null,
    nameFr: substructureMaterialClass?.nameFr ?? null,
    modulusOfElasticity: substructureMaterialClass?.modulusOfElasticity ?? null,
    substructureMaterial: substructureMaterialClass?.substructureMaterial ?? null,
  };

  const onSubmit = () => async (formData: SubstructureMaterialClassFormBody) => {
    if (!formData.substructureMaterial) return;

    const body: InsertSubstructureMaterialClassBody | UpdateSubstructureMaterialClassBody = {
      name: formData.name ?? "",
      nameIt: formData.nameIt || null,
      nameDe: formData.nameDe || null,
      nameEs: formData.nameEs || null,
      nameFr: formData.nameFr || null,
      modulusOfElasticity: formData.modulusOfElasticity,
      substructureMaterialCode: formData.substructureMaterial.code,
    };

    try {
      if (substructureMaterialClass) {
        await substructureMaterialClassesApi.updateDeckSubstructureMaterialClass(substructureMaterialClass.id, body);
      } else {
        await substructureMaterialClassesApi.insertDeckSubstructureMaterialClass(body);
      }
      dispatch(showSuccessNotification(i18n.translation));
      onClose();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  return (
    <ModalDialog
      onClose={onClose}
      title={
        substructureMaterialClass
          ? `${i18n.translation.common.edit} ${i18n.translation.deckDesignerModule.substructureMaterialClasses.singular}`
          : `${i18n.translation.common.add} ${i18n.translation.deckDesignerModule.substructureMaterialClasses.singular}`
      }
    >
      <Form<SubstructureMaterialClassFormBody>
        submitText={i18n.translation.common.save}
        onSubmit={onSubmit}
        defaultValues={defaultValues}
      >
        {(form) => (
          <FormFieldsContainer fullWidth>
            <TranslatableTextField form={form} />
            <Controller
              name="substructureMaterial"
              control={form.control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.deckDesignerModule.substructureMaterials.singular}
                  error={fieldState.error}
                >
                  {({ labelId }) => (
                    <AsyncSelect<SubstructureMaterial, false>
                      {...field}
                      {...substructureMaterialOptions}
                      inputId={labelId}
                      isMulti={false}
                      placeholder={`${i18n.translation.common.select}...`}
                      getOptionLabel={(option) => option.name}
                      getOptionValue={(option) => option.code}
                      isInvalid={fieldState.invalid}
                      onChange={(option) => field.onChange(option)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="modulusOfElasticity"
              control={form.control}
              rules={{ min: 0 }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.deckDesignerModule.substructureMaterialClasses.modulusOfElasticity}
                  error={fieldState.error}
                  isOptional
                >
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      min={0}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        )}
      </Form>
    </ModalDialog>
  );
};

export default SubstructureMaterialClassForm;
